<template>
  <div class="filtered-gallery">
    <div class="filter-controls">
      <div class="filter-buttons">
        <button 
          v-for="category in categories" 
          :key="category.value"
          :class="['filter-btn', { active: selectedCategory === category.value }]"
          @click="handleCategoryChange(category.value)"
        >
          {{ category.name }}
        </button>
      </div>
    </div>
    
    <div v-if="filteredImages.length" class="gallery-grid" v-viewer>
        <div 
            v-for="image in filteredImages" 
            :key="image.itemImageSrc"
            class="gallery-item"
        >
            <img 
              :src="image.itemImageSrc" 
              :alt="image.alt"
            />
        </div>
    </div>

        <div v-else class="no-results">
            {{ $t('gallery.noImages') }}
        </div>

    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import 'viewerjs/dist/viewer.css';
import VueViewer from 'v-viewer';
// Import all images using require.context
const galleryContext = require.context('@/assets/gallery', true, /\.(jpg|jpeg|png)$/i);

export default {
  name: 'FilteredGallery',
  directives: {
    viewer: VueViewer.directive
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const images = ref([]);
    const showFullScreen = ref(false);
    const selectedImage = ref(null);

    // Get category from route params
    const getCurrentCategory = () => {
      const category = route.params.category;
      return category || 'all';
    };

        const selectedCategory = ref(getCurrentCategory());

        const categories = [
            { name: t('gallery.apartment1'), value: 'ap1' },
            { name: t('gallery.apartment2'), value: 'ap2' },
            { name: t('gallery.poprad'), value: 'poprad' },
            { name: t('gallery.paradise'), value: 'paradise' },
            { name: t('gallery.tatr'), value: 'tatr' }
        ];

    // Watch for route params changes
    watch(() => route.params.category, (newCategory) => {
      selectedCategory.value = newCategory || 'all';
    });

        const handleCategoryChange = (category) => {
            const locale = route.path.split('/')[1];
            const basePath = route.path.split('/')[2]
            router.push(`/${locale}/${basePath}/${category}`);
        };

        const openImage = (image) => {
            selectedImage.value = image;
            showFullScreen.value = true;
        };

        const loadGalleryImages = () => {
            const groupedImages = {};

            galleryContext.keys().forEach(path => {
                const pathParts = path.split('/');
                const category = pathParts[1];
                const imageName = pathParts[pathParts.length - 1].split('.')[0];

                if (!groupedImages[category]) {
                    groupedImages[category] = [];
                }

                groupedImages[category].push({
                    itemImageSrc: galleryContext(path),
                    thumbnailImageSrc: galleryContext(path),
                    alt: `${category} - ${imageName}`,
                    title: t(`gallery.${category}`),
                    category: category,
                });
            });

            images.value = Object.values(groupedImages).flat();
        };

        const filteredImages = computed(() => {
            if (selectedCategory.value === 'all') {
                return images.value;
            }
            return images.value.filter(img => img.category === selectedCategory.value);
        });

        onMounted(() => {
            loadGalleryImages();
        });

        return {
            categories,
            selectedCategory,
            filteredImages,
            handleCategoryChange,
            showFullScreen,
            selectedImage,
            openImage
        };
    }
};
</script>

<style scoped>

.filtered-gallery {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 1rem;
}

.filter-controls {
    margin-bottom: 2rem;
    padding: 1rem 0;
}

.filter-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: .5rem;
    padding: 0.5rem;
    min-height: 3em;
}

.filter-btn {
    background: transparent;
    border: 1px solid var(--primary-color, #4a90e2);
    color: var(--primary-color, #4a90e2);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;
    font-weight: 500;
    white-space: nowrap;
}

.filter-btn:hover,
.filter-btn.active {
    background: var(--primary-color, #4a90e2);
    color: white;
}

/* Gallery Grid */
.gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: auto;
    padding: 0.5rem;
}

.gallery-item {
    position: relative;
    aspect-ratio: 3/2;
    overflow: hidden;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    margin: 0.5rem;
}

.gallery-item:hover {
    transform: translateY(-5px) scale(1.05) !important; 
    z-index: 2;
    opacity: 1 !important;
    transition: opacity 0.3s, transform 0.3s;
}

.gallery-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.gallery-item:hover .image-overlay {
    opacity: 1;
}


.fullscreen-image-container {
    overflow-y: hidden;
    position: relative;
    width: 100%;
    height: 100%;
    background: var(--primary-color);
    border-radius: 10px;
    border: 1px solid var(--primary-color);
}


.no-results {
    text-align: center;
    padding: 3rem;
    background-color: var(--primary-color);
    border-radius: 8px;
    font-size: 1.2rem;
    color: white;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
    .filtered-gallery {
        padding: 0;
    }

    .filter-controls {
        margin-bottom: 1rem;
        padding: 0.5rem;
        overflow: visible;
    }

    .filter-buttons {
        padding: 0.5rem;
        flex-wrap: wrap;
        gap: 0.5rem;
        height: auto;
        min-height: 2.5em;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .filter-btn {
        font-size: 0.9rem;
        padding: 0.4rem 0.8rem;
        margin: 0.2rem;
        white-space: nowrap;
    }

    .gallery-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 0.75rem;
        padding: 0.75rem;
    }

    .image-overlay {
        opacity: 1;
        font-size: 0.9rem;
    }

    :deep(.image-dialog) {
        margin: 0;
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
    }
}

@media (max-width: 350px) {}
</style>